import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Clock, ExternalLink, Send } from 'lucide-react';
import { toast } from 'sonner';
import type { InboxMessage, InboxStatus, InboxThreadDetail } from '../../types/inbox';
import type { CurrentUser } from '../../types/user';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { CHANNEL_LABEL, formatExactTimestamp } from '../../lib/inboxView';
import { formatRelativeTime } from '../../lib/relativeTime';
import { isClaimedByOther } from '../../lib/inboxClaim';
import {
  dmReplyWindowState,
  dmWindowBlocksSend,
  dmWindowClosedMessage,
  dmWindowCountdownMessage,
  dmWindowPassedWarning,
  resolveLastInboundAt,
} from '../../lib/inboxDmWindow';
import { useNowMs } from '../../lib/useNowMs';
import { assignThread, addThreadNote, sendDmMessage, setThreadPriority, setThreadStatus } from './api';
import {
  CollisionBanner,
  DetailHeader,
  NotesPanel,
  NotesToggle,
  PriorityToggle,
  WorkflowSelects,
  useComposerClaim,
} from './threadDetailShared';
import { MessageAttachments } from '../../components/social/MessageAttachments';
import { SuggestReplyPanel } from './SuggestReplyPanel';
import type { AccountExternalLinks } from '../../types/account';

// Community Inbox Phase 2 — DM detail: a conversation view (incoming left /
// outgoing right, attachments rendered), a send composer, assign/status/notes,
// and a "View on <platform>" link.

export function DmDetailBody({
  detail,
  me,
  users,
  accountNameById,
  accountLinksById,
  onChanged,
}: {
  detail: InboxThreadDetail;
  me: CurrentUser;
  users: CurrentUser[];
  accountNameById: Map<string, string>;
  accountLinksById: Map<string, AccountExternalLinks>;
  onChanged: () => void;
}) {
  const { thread, messages, notes } = detail;
  const [draft, setDraft] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const claim = useComposerClaim(thread.id);

  const nowMs = useNowMs();
  const claimedByOther = isClaimedByOther(thread, me.uid, nowMs);
  // Meta's reply window, run from the CUSTOMER's last message (never
  // lastActivityAt, which our own sends bump). A legacy thread with no stored
  // lastInboundAt derives it from the messages held; with nothing at all the
  // state is 'unknown' and the composer stays OPEN -- the server's
  // window-specific refusal is the safety net. 'agent': this is the human
  // composer, the only DM send path that could carry the HUMAN_AGENT tag.
  const replyWindow = dmReplyWindowState(
    thread.channel,
    resolveLastInboundAt(thread.lastInboundAt, messages),
    nowMs,
    'agent',
  );
  // ⚠️ A PASSED window does not always BLOCK: Instagram only warns, approved
  // tag or not, because an unanswered message request can still be answered
  // past any window and we cannot tell those threads apart. dmWindowBlocksSend is
  // the one decision; `windowBlocked` is what disables the composer.
  const windowPassed = replyWindow.kind === 'closed';
  const windowBlocked = windowPassed && dmWindowBlocksSend(thread.channel);
  const claimHolder = claimedByOther ? users.find((u) => u.uid === thread.claimedByUid) : null;

  const send = useMutation({
    mutationFn: () => sendDmMessage(thread.id, draft.trim()),
    onSuccess: () => {
      setDraft('');
      claim.release();
      toast.success('Message sent.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const status = useMutation({
    mutationFn: (next: InboxStatus) => setThreadStatus(thread.id, next),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const assign = useMutation({
    mutationFn: (uid: string | null) => assignThread(thread.id, uid),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const priority = useMutation({
    mutationFn: (next: boolean) => setThreadPriority(thread.id, next),
    onSuccess: onChanged,
    onError: (err: Error) => toast.error(err.message),
  });
  const note = useMutation({
    mutationFn: () => addThreadNote(thread.id, noteDraft.trim()),
    onSuccess: () => {
      setNoteDraft('');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onSend = () => {
    if (draft.trim() === '' || send.isPending || windowBlocked) return;
    if (
      claimedByOther &&
      !window.confirm(
        `${claimHolder?.displayName ?? 'Another agent'} is already replying to this conversation. Send anyway?`,
      )
    ) {
      return;
    }
    send.mutate();
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200">
      <DetailHeader
        thread={thread}
        accountNameById={accountNameById}
        accountLinks={accountLinksById.get(thread.kyckAccountId)}
        onResolve={() => status.mutate('resolved')}
        resolving={status.isPending}
        extra={
          <>
            {/* Part E3 — DM conversation permalink (populated from the Zernio
                conversation row's `url` when exposed; hidden otherwise). */}
            {thread.permalink && (
              <a
                href={thread.permalink}
                target="_blank"
                rel="noreferrer"
                title={`View on ${CHANNEL_LABEL[thread.channel]}`}
                className="inline-flex items-center gap-1 text-xs text-kyck-700 hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View on {CHANNEL_LABEL[thread.channel]}
              </a>
            )}
            <PriorityToggle
              priority={thread.priority === true}
              disabled={priority.isPending}
              onToggle={() => priority.mutate(thread.priority !== true)}
            />
          </>
        }
      >
        <WorkflowSelects
          thread={thread}
          users={users}
          onStatus={(s) => status.mutate(s)}
          onAssign={(uid) => assign.mutate(uid)}
        />
      </DetailHeader>

      {claimedByOther && (
        <CollisionBanner label={(claimHolder?.displayName || claimHolder?.email) ?? 'Another agent'} />
      )}

      {/* Conversation */}
      <div className="scroll-subtle min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {messages.map((message) => (
          <DmBubble key={message.id} message={message} />
        ))}
        {messages.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-500">No messages in this conversation.</p>
        )}
      </div>

      {notesOpen && (
        <NotesPanel
          notes={notes}
          draft={noteDraft}
          setDraft={setNoteDraft}
          onAdd={() => note.mutate()}
          adding={note.isPending}
        />
      )}

      {/* Send composer */}
      <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3">
        {/* AI drafts for the latest inbound message. Hidden when the customer
            hasn't said anything yet — there is nothing to reply to. */}
        <SuggestReplyPanel
          className="mb-2"
          threadId={thread.id}
          disabled={
            windowBlocked || !messages.some((m) => m.direction === 'incoming' && m.text.trim() !== '')
          }
          onUse={setDraft}
        />
        {/* The window notice sits ABOVE the box so it is read before typing:
            blocked = the reason in the same words the server would use;
            passed but not blocked = a warning, Send stays live; the last
            24 hours = a countdown. */}
        {windowBlocked && (
          <p role="status" className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {dmWindowClosedMessage(thread.channel)}
          </p>
        )}
        {windowPassed && !windowBlocked && (
          <p role="status" className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {dmWindowPassedWarning(thread.channel)}
          </p>
        )}
        {replyWindow.kind === 'open' && replyWindow.closingSoon && (
          <p role="status" className="mb-2 flex items-center gap-1.5 text-xs text-amber-800">
            <Clock className="h-3.5 w-3.5" />
            {dmWindowCountdownMessage(thread.channel, replyWindow.msRemaining)}
          </p>
        )}
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={claim.onFocus}
          placeholder={windowBlocked ? 'Reply window closed' : `Message ${thread.author.name}…`}
          disabled={windowBlocked}
          rows={2}
        />
        <div className="mt-2 flex items-center gap-2">
          <NotesToggle count={notes.length} onToggle={() => setNotesOpen((o) => !o)} />
          <span className="flex-1" />
          <Button size="sm" onClick={onSend} disabled={windowBlocked || draft.trim() === '' || send.isPending}>
            {send.isPending ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

function DmBubble({ message }: { message: InboxMessage }) {
  const incoming = message.direction === 'incoming';
  return (
    <div className={cn('flex', incoming ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-lg rounded-2xl px-3 py-2',
          incoming ? 'bg-slate-100 text-slate-800' : 'bg-kyck-600 text-white',
        )}
      >
        <p className="whitespace-pre-wrap text-sm">{message.text}</p>
        <MessageAttachments attachments={message.attachments} tone={incoming ? 'light' : 'dark'} />
        <div
          className={cn(
            'mt-0.5 text-[11px]',
            incoming ? 'text-slate-400' : 'text-kyck-100/80',
          )}
          title={formatRelativeTime(message.createdAt)}
        >
          {message.authorName} · {formatExactTimestamp(message.createdAt)}
        </div>
      </div>
    </div>
  );
}
